import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";

interface UsePaginationProps {
  queries: any[];
  fetchFunction: () => Promise<any>;
  hasPagination?: boolean;
}

function usePagination({
  queries,
  fetchFunction,
  hasPagination = true,
}: UsePaginationProps) {
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();
  const [total, setTotal] = useState(0);

  const totalPages = Math.ceil(total / itemsPerPage);

  useEffect(() => {
    // Sin paginación no hay "página siguiente" que prefetchear: el pedido sale
    // sin `page`, así que esto disparaba un GET IDÉNTICO al que la pantalla
    // acababa de hacer, lo guardaba bajo otra clave y nunca lo leía. Un request
    // completo de más por cada carga y por cada cambio de búsqueda.
    if (!hasPagination) return;

    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;

      queryClient.prefetchQuery(
        [...queries, nextPage, itemsPerPage, hasPagination],
        fetchFunction,
        {
          staleTime: 60 * 1000,
        }
      );
    }
  }, [currentPage, queryClient, itemsPerPage, total, hasPagination]);

  function next() {
    setOffset((offset) => offset + itemsPerPage);
    setCurrentPage((currentPage) => Math.min(currentPage + 1, totalPages));
  }

  function prev() {
    setOffset((offset) => offset - itemsPerPage);
    setCurrentPage((currentPage) => Math.max(currentPage - 1, 1));
  }

  function jump(page: any) {
    const pageNumber = Math.max(1, page);
    setOffset(() => itemsPerPage * (pageNumber - 1));
    setCurrentPage(() => Math.min(pageNumber, totalPages));
  }

  function reset() {
    setOffset(0);
    setCurrentPage(1);
  }

  return {
    currentPage,
    offset,
    itemsPerPage,
    total,
    totalPages,
    next,
    prev,
    jump,
    setTotal,
    setItemsPerPage,
    reset,
  };
}

export default usePagination;
